// Server warmup utility to keep backend alive
class ServerWarmup {
  private isWarming = false;
  private warmupInterval: number | null = null;
  private lastWarmupTime = 0;
  private readonly baseUrl = import.meta.env.VITE_BACKEND_URL;
  
  private readonly endpoints = [
    '/api/health',
    '/api/status',
    '/api/ping',
    '/'
  ];

  private getRandomDelay(): number {
    // Random delay between 8-20 seconds
    return Math.floor(Math.random() * 12000) + 8000;
  }

  private getRandomEndpoint(): string {
    return this.endpoints[Math.floor(Math.random() * this.endpoints.length)];
  }

  private async makeWarmupRequest(): Promise<void> {
    if (this.isWarming) return;
    
    this.isWarming = true;
    
    try {
      const endpoint = this.getRandomEndpoint();
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);
      
      // Add random headers to look more natural
      const headers: Record<string, string> = {
        'User-Agent': navigator.userAgent,
        'Accept': 'application/json,text/plain,*/*',
        'Accept-Language': navigator.language,
        'Cache-Control': 'no-cache'
      };
      
      // Sometimes add referer
      if (Math.random() > 0.5) {
        headers['Referer'] = window.location.origin;
      }
      
      await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'GET',
        headers,
        signal: controller.signal,
        mode: 'cors',
        credentials: 'omit'
      });
      
      clearTimeout(timeoutId);
      this.lastWarmupTime = Date.now();
      
    } catch (error) {
      // Silently handle errors - server might be cold or endpoint doesn't exist
      console.debug('Warmup request failed:', error);
    } finally {
      this.isWarming = false;
    }
  }

  private scheduleNextWarmup(): void {
    if (this.warmupInterval) {
      clearTimeout(this.warmupInterval);
    }
    
    const delay = this.getRandomDelay();
    this.warmupInterval = window.setTimeout(() => {
      this.makeWarmupRequest();
      this.scheduleNextWarmup();
    }, delay);
  }

  public start(): void {
    // Don't start if already running or if it's been less than 30 seconds since last warmup
    if (this.warmupInterval || (Date.now() - this.lastWarmupTime < 30000)) {
      return;
    }
    
    // Initial warmup after a short delay
    window.setTimeout(() => {
      this.makeWarmupRequest();
      this.scheduleNextWarmup();
    }, Math.floor(Math.random() * 5000) + 3000); // 3-8 seconds
  }

  public stop(): void {
    if (this.warmupInterval) {
      clearTimeout(this.warmupInterval);
      this.warmupInterval = null;
    }
    this.isWarming = false;
  }

  public isActive(): boolean {
    return this.warmupInterval !== null;
  }
}

// Singleton instance
const serverWarmup = new ServerWarmup();

// Auto-start when page becomes visible
document.addEventListener('visibilitychange', () => {
  if (!document.hidden && !serverWarmup.isActive()) {
    serverWarmup.start();
  }
});

// Stop when page is about to unload
window.addEventListener('beforeunload', () => {
  serverWarmup.stop();
});

export default serverWarmup;
